import { TFunction } from "i18next";
import * as yup from "yup";

// The password policy, in one place. PasswordValidation renders PASSWORD_RULES
// as the checklist; the SignUp and Account forms validate with passwordSchema,
// which is built from the same list.

export const PASSWORD_MIN_LENGTH = 6;

export interface PasswordRule {
  /** Checklist label: an `errorPasswordSignup.*` key. */
  label: string;
  /** Form error: an `error.*` key. */
  message: string;
  /** Interpolation values both texts need. */
  values?: Record<string, number>;
  test: (value: string) => boolean;
}

// Checklist order. Forms report the first failing rule in this order too.
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    label: "errorPasswordSignup.length",
    message: "error.stringLonger",
    values: { length: PASSWORD_MIN_LENGTH },
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    label: "errorPasswordSignup.alphabetical",
    message: "error.hasAlphabetical",
    test: (value) => /[a-zA-Z]/.test(value),
  },
  {
    label: "errorPasswordSignup.numeric",
    message: "error.hasNumeric",
    test: (value) => /[0-9]/.test(value),
  },
  {
    label: "errorPasswordSignup.symbol",
    message: "error.hasSymbol",
    test: (value) => /[^a-zA-Z0-9\s]/.test(value),
  },
  {
    label: "errorPasswordSignup.whiteSpaces",
    message: "error.whiteSpaces",
    test: (value) => /^\S*$/.test(value),
  },
];

// Adds every rule to `base` as a yup test. Presence stays with the caller:
// pass `yup.string().required(...)` as `base` when an empty field should say
// "required" rather than "too short".
export const passwordSchema = (
  t: TFunction,
  base: yup.StringSchema = yup.string(),
) =>
  PASSWORD_RULES.reduce(
    (schema, rule) =>
      schema.test({
        name: rule.label,
        message: t(rule.message, rule.values),
        test: (value) => value == null || rule.test(value),
      }),
    base,
  );
